import styled, { css } from 'styled-components';

import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

import { ContainerOT } from '../MainStyledComponents';

export const ContainerOTStyled = styled(ContainerOT)`
  /* Feedback usuario 2026-07-06: el tinte lila (#aeadf01f) se confundía con
     la card padre (backgroundLila). Hueco libre de la paleta de hijas:
     padre=lila, Actuación=gris, Accidente=rosado, Comunicación=verdoso →
     el tramo toma el AZUL CIELO (familia "en curso" del RV). Mismo mecanismo
     que el resto: color de theme con opacidad (blueCommunication al 30%). */
  background-color: ${({ theme }) => `${theme.colors.blueCommunication}4D`};
`;

// V4 D7 — chip del `pauseCause` con color según causa:
// - basic → azul (primary)
// - action → rojo (error)
// - external-call → dorado (gold) — feedback usuario 2026-07-08: rojo y
//   `secondaryOrange` (naranja) se leían demasiado parecidos en un vistazo
//   rápido (mismo problema que los dots del mapa, ver `getPauseCauseColor`
//   en MapWithSegments — MISMO token nuevo en ambos sitios para no romper
//   la consistencia chip↔mapa).
/* Feedback usuario 2026-07-04: misma geometría que el chip de ESTADO
   (SpecialTag Container: pill border-radius 100rem, padding equivalente,
   texto XS sin uppercase). Solo cambian los colores semánticos D7. */
export const PauseCauseChip = styled.span<{ cause?: string; $small?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  border-radius: 100rem;
  /* Fuente responsive IGUAL que el <Text> de SpecialTag (XS en default, XXXS
     en small): antes era XS fija y el texto de "Causa: X" no encogia como el
     de las etiquetas hermanas al pasar a small (feedback usuario 2026-07-31). */
  font-size: ${({ theme, $small }) =>
    $small ? theme.fontSize.XXXS : theme.fontSize.XS};
  color: ${({ theme }) => theme.colors.white};

  /* Responsive IGUAL que los SpecialTag hermanos de la fila de chips
     (sizeTag small/default): small = height 1.8rem en landscape tablet,
     default = 3rem en el resto, para que la pill de causa deje de desencajar
     al lado del tag de estado y de "Resuelto por". Antes tenia padding fijo
     (0.4rem 1.6rem) y salia mas alta/grande que las demas en horizontal
     (feedback usuario 2026-07-31 "no tiene responsive"). */
  ${({ $small, theme }) =>
    $small
      ? css`
          height: 1.8rem;
          padding: 0 ${theme.spacing.s};
        `
      : css`
          height: 3rem;
          padding: 0 2rem;
        `}

  ${({ theme, cause }) => {
    switch (cause) {
      case 'action':
        // Mismo rojo que la etiqueta «Anulada»: la letra blanca llega a 4,6:1.
        return css`
          background: ${theme.colors.tagSurfaceRed};
        `;
      case 'external-call':
        return css`
          background: ${theme.colors.gold};
          color: ${theme.colors.tagTextGold};
        `;
      case 'basic':
      default:
        return css`
          background: ${theme.colors.primary};
        `;
    }
  }}
`;

// P15 rework bidireccional (2026-07-09): las pills de vínculo ya NO son este
// `LinkedPill` gris/ámbar/azul hand-rolled — pasaron a `SpecialTag` con
// variants de color semántico (`LinkedFromSegment`/`PendingLink`/`LinkCta`) +
// mini-iconos ver/editar inline (`TagActionIcon`). Ver
// docs/superpowers/plans/2026-07-09-p15-rework-canonical-relate.md. Desde el
// 2026-09-24 las CTA también viven en el grupo de pills de la cabecera (se fue
// `LinkedPillsRow`, la fila aparte bajo el título).

/**
 * Descripción + lápiz "Corregir el tramo" EN LA MISMA FILA (feedback usuario
 * 2026-08-05), copiando el `ContainerDescription` de la card de la Orden de
 * Trabajo: el textarea cede el ancho del botón y el lápiz se apoya en su
 * esquina inferior derecha, en vez de ocupar una línea entera debajo.
 *
 * `justify-content: flex-end` sostiene el caso sin descripción: el tramo puede
 * no tenerla y el lápiz debe seguir apareciendo, pegado a la derecha.
 */
export const DescriptionRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 2.9rem;
  width: 100%;

  /* Sin esto el InputTextArea encoge a su ancho natural y deja un hueco en
     medio: el textarea debe quedarse con todo el ancho sobrante. */
  & > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

/**
 * Bloque de fotos del tramo (intervención leve, 2026-08-09).
 *
 * `PhotosRow` es el `ImagesContainer` del canónico de Actuación
 * (`ActionEdition/styles.ts`): columna, alineado a la izquierda y con su mismo
 * hueco.
 *
 * El botón de añadir va en `PhotosHeader`, junto al título y CENTRADO con él.
 * En el formulario de Actuación ese botón redondo acompaña a una pila estrecha
 * de 30rem; aquí el contenido es una rejilla fluida, y meterlo en la misma
 * fila la aprieta a una columna y expulsa el botón fuera de la card.
 *
 * Lo único propio es `min-width: 0`, y no es decorativo: sin él la rejilla de
 * miniaturas empuja el ancho de la card y desborda en tablet vertical.
 */
export const PhotosRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${({ theme }) => theme.spaces.xxxs}rem;
  width: 100%;
  min-width: 0;
`;

export const PhotosHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spaces.xxxs}rem;
`;

/**
 * El `+` de añadir fotos, a la medida del título que acompaña.
 *
 * `ButtonIconBackground` mide 3,4rem fijos, y el `rem` de la app NO escala con
 * el viewport (`html: 62.5%`), así que son 34 px siempre. Junto a una pila de
 * 30rem —su sitio en el formulario de Actuación— queda proporcionado; junto a
 * un título corto, en tablet, se come la línea (feedback en tablet real).
 *
 * Ajuste LOCAL, que es como la app resuelve estos casos: `LocationForm` y
 * `OpenCameraModal` ya envuelven este mismo botón para adaptarlo a su sitio.
 */
export const PhotosAddButton = styled(ButtonIconBackground)`
  width: 2.6rem;
  height: 2.6rem;
  align-self: center;

  img {
    width: 1.4rem;
    height: 1.4rem;
  }
`;
